import { AttachmentPrimitive } from '@assistant-ui/react';
import { useEffect, useState } from 'react';

import { CardRemoveBtn } from '@playbooks/molecules/buttons';
import { computeBytes } from '@playbooks/utils';

import { FileAttachmentCard, ImageAttachmentCard, PDFAttachmentCard } from 'components/agent/attachment-card';

const ComposerAttachment = ({ attachment, onImageRegister, onImageUnregister, onView }) => {
	const [preview, setPreview] = useState('');
	const file = attachment.file;
	const contentType = file?.type || attachment.contentType || '';
	const isImage = attachment.type === 'image' || contentType.startsWith('image/');
	const isPdf = contentType === 'application/pdf' || attachment.name?.toLowerCase().endsWith('.pdf');
	const size = file?.size !== undefined ? computeBytes(file.size) : null;
	const image = {
		id: attachment.id,
		name: attachment.name,
		contentType,
		preview,
	};

	// Hooks
	useEffect(() => {
		if (!isImage) return;
		if (!attachment.id || !attachment.name || !file) {
			throw new Error('Agent composer image attachments require id, name, and file.');
		}

		const url = URL.createObjectURL(file);
		setPreview(url);
		onImageRegister({
			id: attachment.id,
			name: attachment.name,
			contentType,
			preview: url,
		});

		return () => {
			onImageUnregister(attachment.id);
			URL.revokeObjectURL(url);
		};
	}, [attachment.id, attachment.name, contentType, file, isImage, onImageRegister, onImageUnregister]);

	// Actions
	const onClick = () => {
		if (isImage && preview) onView(image);
	};

	const onKeyDown = event => {
		if (!isImage || !preview) return;
		if (!['Enter', ' '].includes(event.key)) return;
		event.preventDefault();
		onView(image);
	};

	const onRemoveClick = event => {
		event.stopPropagation();
	};

	return (
		<AttachmentPrimitive.Root
			className={
				isImage ? 'group relative flex h-14 w-14 shrink-0 cursor-pointer' : 'group relative flex h-14 min-w-0 max-w-64'
			}
			role={isImage ? 'button' : undefined}
			tabIndex={isImage ? 0 : undefined}
			onClick={onClick}
			onKeyDown={onKeyDown}>
			{isImage && <ImageAttachmentCard attachment={attachment} preview={preview} />}
			{isPdf && <PDFAttachmentCard attachment={attachment} size={size} />}
			{!isImage && !isPdf && <FileAttachmentCard attachment={attachment} size={size} />}
			<AttachmentPrimitive.Remove render={<CardRemoveBtn aria-label='Remove attachment' onClick={onRemoveClick} />} />
		</AttachmentPrimitive.Root>
	);
};

export { ComposerAttachment };
